import { useState, forwardRef, useCallback } from 'react';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

/**
 * Format number to Indonesian currency format (with dots as thousand separators)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted string (e.g., "1.500.000")
 */
export const formatCurrencyInput = (value) => {
  if (!value && value !== 0) return '';
  
  // Remove all non-numeric characters
  const numericValue = String(value).replace(/\D/g, '');
  
  if (!numericValue) return '';
  
  // Format with dots as thousand separators
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Parse formatted currency string back to number
 * @param {string} value - The formatted string (e.g., "1.500.000")
 * @returns {number} The numeric value
 */
export const parseCurrencyInput = (value) => {
  if (!value) return 0;
  return parseInt(String(value).replace(/\./g, ''), 10) || 0;
};

/**
 * Currency Input Component with automatic formatting
 * Shows "Rp" prefix and formats number with thousand separators
 */
const CurrencyInput = forwardRef(({ 
  value, 
  onChange, 
  className,
  placeholder = "0",
  ...props 
}, ref) => {
  // Initialize display value from prop
  const getInitialDisplayValue = useCallback(() => {
    if (value !== undefined && value !== null && value !== '') {
      return formatCurrencyInput(value);
    }
    return '';
  }, [value]);

  const [displayValue, setDisplayValue] = useState(getInitialDisplayValue);
  
  // Track if this is the first render or if value changed externally
  const [lastPropValue, setLastPropValue] = useState(value);
  
  // Sync with prop changes (e.g., when form is reset or value is set programmatically)
  if (value !== lastPropValue) {
    const newDisplayValue = value !== undefined && value !== null && value !== '' 
      ? formatCurrencyInput(value) 
      : '';
    setDisplayValue(newDisplayValue);
    setLastPropValue(value);
  }

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Remove all non-numeric characters
    const numericOnly = inputValue.replace(/\D/g, '');
    
    // Format with thousand separators
    const formatted = formatCurrencyInput(numericOnly);
    setDisplayValue(formatted);
    
    // Call onChange with the numeric value
    if (onChange) {
      const numericValue = parseCurrencyInput(numericOnly);
      // Create synthetic event-like object
      onChange({
        target: {
          name: props.name,
          value: numericValue
        }
      });
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">
        Rp
      </span>
      <Input
        ref={ref}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        className={cn("pl-10", className)}
        placeholder={placeholder}
        {...props}
      />
    </div>
  );
});

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;
